import {
  warningBg,
  alertBg,
  infoBg,
  successBg,
  topBarBg,
  defaultRoleBg,
} from '../mainTheme';

// Shared chip color shape for both role taxonomies.
export interface RoleColor {
  backgroundColor: string;
  borderColor: string;
}

// --- Facility Roles (relationship to facility/location) ---

export const FACILITY_ROLE_VALUE = {
  LOCATION_ADMIN: 1,
  SCHEDULER: 2,
  CAREGIVER: 3,
  PATIENT: 4,
  NURSE: 5,
} as const;

export type FacilityRoleValue =
  (typeof FACILITY_ROLE_VALUE)[keyof typeof FACILITY_ROLE_VALUE];

export const FACILITY_ROLES = [
  {
    id: FACILITY_ROLE_VALUE.LOCATION_ADMIN,
    label: 'Location Admin',
    color: { backgroundColor: alertBg },
  },
  {
    id: FACILITY_ROLE_VALUE.SCHEDULER,
    label: 'Facility Scheduler',
    color: { backgroundColor: warningBg }, // TODO: revisit color
  },
  {
    id: FACILITY_ROLE_VALUE.CAREGIVER,
    label: 'Caregiver',
    color: { backgroundColor: infoBg },
  },
  {
    id: FACILITY_ROLE_VALUE.PATIENT,
    label: 'Patient',
    color: { backgroundColor: topBarBg }, // TODO: revisit color
  },
  {
    id: FACILITY_ROLE_VALUE.NURSE,
    label: 'Nurse',
    color: { backgroundColor: infoBg }, // TODO: revisit color
  },
];

export const getFacilityRole = (id: number) =>
  FACILITY_ROLES.find((role) => role.id === id);

// FACILITY_ROLES chips are borderless, so borderColor mirrors the background.
export const getFacilityRoleColor = (id: number): RoleColor => {
  const backgroundColor =
    getFacilityRole(id)?.color.backgroundColor ?? defaultRoleBg;
  return { backgroundColor, borderColor: backgroundColor };
};

// --- Careteam Roles (relationship to patient) ---

export const CARETEAM_ROLE_VALUE = {
  PROFESSIONAL_CAREGIVER: 1,
  FAMILY_ADMIN: 2,
  FAMILY_CAREGIVER: 3,
  SOCIAL_CONTACT: 4,
} as const;

export type CareteamRoleValue =
  (typeof CARETEAM_ROLE_VALUE)[keyof typeof CARETEAM_ROLE_VALUE];

export interface CareteamRole {
  id: CareteamRoleValue;
  label: string;
  description: string;
  color: RoleColor;
}

// Colors come from the Figma role-chip design (mainTheme tokens). Chips render
// borderless there, so borderColor mirrors the background.
export const CARETEAM_ROLES: CareteamRole[] = [
  {
    id: CARETEAM_ROLE_VALUE.PROFESSIONAL_CAREGIVER,
    label: 'Professional Caregiver',
    description: 'A professional caregiver assigned to the patient.',
    color: { backgroundColor: infoBg, borderColor: infoBg },
  },
  {
    id: CARETEAM_ROLE_VALUE.FAMILY_ADMIN,
    label: 'Family Administrator',
    description:
      'Full access rights to an individual patient\u2019s information.',
    color: { backgroundColor: successBg, borderColor: successBg },
  },
  {
    id: CARETEAM_ROLE_VALUE.FAMILY_CAREGIVER,
    label: 'Caregiver',
    description: 'Provides care for patients via the Hub.',
    color: { backgroundColor: infoBg, borderColor: infoBg },
  },
  {
    id: CARETEAM_ROLE_VALUE.SOCIAL_CONTACT,
    label: 'Social Contact',
    description: 'An individual who may call the patient through the Hub.',
    color: { backgroundColor: topBarBg, borderColor: topBarBg },
  },
];

// Neutral grey for an unrecognized role id — not a real chip color.
const DEFAULT_CARETEAM_ROLE_COLOR: RoleColor = {
  backgroundColor: defaultRoleBg,
  borderColor: defaultRoleBg,
};

export const getCareteamRole = (id: number): CareteamRole | undefined =>
  CARETEAM_ROLES.find((role) => role.id === id);

export const getCareteamRoleColor = (id: number): RoleColor =>
  getCareteamRole(id)?.color ?? DEFAULT_CARETEAM_ROLE_COLOR;

export const getCareteamRoleLabel = (id: number): string =>
  getCareteamRole(id)?.label ?? 'unknown';
